import { Accordion, Box, ChervonDownIcon, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage } from "react-intl";

interface HomeAnalyticsCardProps {
  testId?: string;
  title: string;
  children?: React.ReactNode;
  /** Rendered under the "Today" caption. */
  secondaryLine?: React.ReactNode;
  /** When given, the card becomes expandable and reveals this on expand. */
  detail?: React.ReactNode;
  /** Called the first time the card is expanded, so detail can be fetched lazily. */
  onFirstExpand?: () => void;
}

const CardHeader = ({
  title,
  secondaryLine,
  children,
}: Pick<HomeAnalyticsCardProps, "title" | "secondaryLine" | "children">) => (
  <>
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Text size="large" variant="body">
        {title}
      </Text>
      <Text size="medium" variant="caption" color="default1">
        <FormattedMessage id="zWgbGg" defaultMessage="Today" />
      </Text>
      {secondaryLine}
    </Box>
    <Text as="h4" variant="heading">
      {children}
    </Text>
  </>
);

export const HomeAnalyticsCard = ({
  children,
  title,
  testId,
  secondaryLine,
  detail,
  onFirstExpand,
}: HomeAnalyticsCardProps) => {
  const hasExpanded = React.useRef(false);
  const [expanded, setExpanded] = React.useState(false);

  const handleValueChange = (value: string) => {
    setExpanded(Boolean(value));

    if (value && !hasExpanded.current) {
      hasExpanded.current = true;
      onFirstExpand?.();
    }
  };

  if (!detail) {
    return (
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        borderRadius={3}
        paddingX={3}
        paddingY={5}
        display="flex"
        justifyContent="space-between"
        data-test-id={testId}
      >
        <CardHeader title={title} secondaryLine={secondaryLine}>
          {children}
        </CardHeader>
      </Box>
    );
  }

  // One uncontrolled root per card: AccordionRootProps is single-value only
  // (it has no `type` prop), so two cards cannot share a root and stay
  // independently expandable.
  return (
    <Accordion
      borderWidth={1}
      borderStyle="solid"
      borderColor="default1"
      borderRadius={3}
      // Grid items are min-width:auto, so without this the card grows to fit
      // its longest row instead of letting that row ellipsize.
      minWidth={0}
      data-test-id={testId}
      onValueChange={handleValueChange}
    >
      <Accordion.Item value="detail">
        <Accordion.Trigger paddingX={3} paddingY={5} gap={6}>
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            gap={3}
          >
            <CardHeader title={title} secondaryLine={secondaryLine}>
              {children}
            </CardHeader>
          </Box>
          <Box
            as="span"
            display="flex"
            color="default2"
            flexShrink="0"
            data-test-id="expand-card"
            __transform={expanded ? "rotate(180deg)" : undefined}
          >
            <ChervonDownIcon />
          </Box>
        </Accordion.Trigger>
        <Accordion.Content>{detail}</Accordion.Content>
      </Accordion.Item>
    </Accordion>
  );
};
